
import Vue from 'vue';
import Component from 'vue-class-component';
import axios from 'axios';
import {settings} from '@/plugins/settings';

interface IMessageType {
  slug: string;
  name: string;
}

interface IPreviewParams {
  viewPath: string;
  lang: string;
  channelId: number;
  title?: string;
  orderType?: string;
}

interface ILangLinks {
  [lang: string]: IPreviewParams;
}

interface IPushLangEntry {
  titleViewPath: string | null;
  bodyViewPath: string | null;
  lang: string;
  channelId: number;
}

interface IPushLangLinks {
  [lang: string]: IPushLangEntry;
}

interface IMessageRow {
  name: string;
  emails: { [typeSlug: string]: ILangLinks };
  sms: { [typeSlug: string]: ILangLinks };
  push: { [typeSlug: string]: IPushLangLinks };
  types: IMessageType[];
}

interface IChannel {
  id: number;
  name: string;
}

@Component({ name: 'Messages' })
export default class Messages extends Vue {

  protected loading: boolean = false;
  protected error: string = '';

  protected isAdmin: boolean = false;
  protected channels: IChannel[] = [];
  protected selectedChannelId: number | null = null;
  protected rows: IMessageRow[] = [];

  protected previewDialog: boolean = false;
  protected previewLoading: boolean = false;
  protected previewError: string = '';
  protected previewHtml: string = '';
  protected previewTitle: string = '';

  created() {
    this.loadMessages();
  }

  protected async loadMessages() {
    this.loading = true;
    this.error = '';
    this.rows = [];

    try {
      const params: any = {};
      if (this.selectedChannelId) {
        params.channelId = this.selectedChannelId;
      }

      const resp = await axios.get(settings.endpoint + 'settings/messages', { params });
      const data = resp.data.data;

      this.isAdmin = !!data.isAdmin;
      this.channels = data.channels || [];
      if (!this.selectedChannelId) {
        this.selectedChannelId = data.currentChannelId || null;
      }

      this.rows = this.parseMessages(data.messages || {});
    } catch (e) {
      this.error = (e && (e as any).response && (e as any).response.data && (e as any).response.data.message)
        || this.$t('settings.messages.loadError').toString();
    } finally {
      this.loading = false;
    }
  }

  protected parseMessages(raw: any): IMessageRow[] {
    const result: IMessageRow[] = [];
    Object.keys(raw).forEach((name: string) => {
      const entry = raw[name];
      result.push({
        name,
        emails: entry.emails || {},
        sms: entry.sms || {},
        push: entry.push || {},
        types: entry.types || [],
      });
    });
    return result;
  }

  protected hasLinks(langMap: ILangLinks | undefined): boolean {
    return !!langMap && Object.keys(langMap).length > 0;
  }

  protected hasPushLinks(langMap: IPushLangLinks | undefined): boolean {
    if (!langMap) return false;
    return Object.values(langMap).some((e) => !!e.bodyViewPath || !!e.titleViewPath);
  }


  protected async openPushPreview(entry: IPushLangEntry, fallbackTitle: string) {
    this.previewTitle = fallbackTitle;
    this.previewHtml = '';
    this.previewError = '';
    this.previewLoading = true;
    this.previewDialog = true;

    try {
      const base = { lang: entry.lang, channelId: entry.channelId };

      const [bodyResp, titleResp] = await Promise.all([
        entry.bodyViewPath
          ? axios.get(settings.endpoint + 'settings/messages/preview', { params: { ...base, viewPath: entry.bodyViewPath } })
          : Promise.resolve(null),
        entry.titleViewPath
          ? axios.get(settings.endpoint + 'settings/messages/preview', { params: { ...base, viewPath: entry.titleViewPath } })
          : Promise.resolve(null),
      ]);

      const bodyHtml  = (bodyResp  && bodyResp.data  && bodyResp.data.data  && bodyResp.data.data.html)  || '';
      const titleHtml = (titleResp && titleResp.data && titleResp.data.data && titleResp.data.data.html) || '';

      if (!bodyHtml && !titleHtml) {
        this.previewError = this.$t('settings.messages.previewError').toString();
        return;
      }

      this.previewHtml = titleHtml
        ? `<div class="push-preview-title">${titleHtml}</div><hr class="my-2"/>${bodyHtml}`
        : bodyHtml;
    } catch (e) {
      this.previewError = this.$t('settings.messages.previewError').toString();
    } finally {
      this.previewLoading = false;
    }
  }

  protected async openPreview(entry: IPreviewParams, fallbackTitle: string) {
    this.previewTitle = entry.title || fallbackTitle;
    this.previewHtml = '';
    this.previewError = '';
    this.previewLoading = true;
    this.previewDialog = true;

    try {
      const params: any = {
        viewPath:  entry.viewPath,
        lang:      entry.lang,
        channelId: entry.channelId,
      };
      if (entry.orderType) {
        params.orderType = entry.orderType;
      }

      const resp = await axios.get(settings.endpoint + 'settings/messages/preview', { params });
      const data = resp.data.data;

      if (data && data.html) {
        this.previewHtml = data.html;
        if (data.title) {
          this.previewTitle = data.title;
        }
      } else {
        this.previewError = this.$t('settings.messages.previewError').toString();
      }
    } catch (e) {
      this.previewError = this.$t('settings.messages.previewError').toString();
    } finally {
      this.previewLoading = false;
    }
  }
}
